import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, ChevronLeft, ChevronRight, Upload } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EQUIP = ["Dry Van", "Reefer", "Flatbed", "Step-Deck", "Power Only", "Box Truck"];
const SVC = ["FTL", "LTL", "Cross-Border", "Drayage", "Flatbed", "White Glove"];

export default function RequestQuote() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [file, setFile] = useState(null);
  const [f, setF] = useState({
    pickup_city: "", pickup_state: "", pickup_zip: "", pickup_date: "",
    delivery_city: "", delivery_state: "", delivery_zip: "", delivery_date: "",
    equipment_type: "Dry Van", service_type: "FTL", commodity: "",
    weight_lbs: "", length_ft: "", width_ft: "", height_ft: "", pieces: 1,
    special_instructions: "",
    contact_name: "", contact_email: "", contact_phone: "", company_name: "",
  });

  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    setLoading(true);
    try {
      const payload = { ...f, weight_lbs: parseFloat(f.weight_lbs) || 0, pieces: parseInt(f.pieces) || 1,
        length_ft: f.length_ft ? parseFloat(f.length_ft) : null,
        width_ft: f.width_ft ? parseFloat(f.width_ft) : null,
        height_ft: f.height_ft ? parseFloat(f.height_ft) : null };
      const { data } = await api.post("/quotes", payload);
      setDone(data);
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("document_type", "quote_attachment");
        fd.append("reference_id", data.quote_id);
        try {
          await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch (uploadErr) {
          console.error("[RequestQuote] attachment upload failed:", uploadErr);
          toast.error("Quote submitted, but attachment failed to upload. Please email it to supplychain@apexscs.ca");
        }
      }
      toast.success(`Quote ${data.quote_id} submitted`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Submit failed"); }
    finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="min-h-[70vh] bg-gray-50 py-20 px-4">
        <div className="max-w-2xl mx-auto bg-white border border-gray-200 rounded-lg p-10 text-center" data-testid="quote-success">
          <CheckCircle2 className="h-14 w-14 text-[#2E7BC7] mx-auto" />
          <h2 className="mt-6 font-display font-black text-3xl text-[#0B3D6E]">Quote request received</h2>
          <p className="mt-3 text-slate-600">Your reference is <span className="font-mono font-bold text-[#0B3D6E]">{done.quote_id}</span></p>
          <div className="mt-6 rounded-md bg-blue-50 border border-blue-100 p-5 text-left">
            <div className="font-display font-bold text-[#0B3D6E]">What happens next</div>
            <ul className="mt-3 space-y-2 text-sm text-slate-700 list-disc list-inside">
              <li>Our operations team reviews your request within business hours.</li>
              <li>We'll <span className="font-semibold">call or email you back</span> with a firm rate — typically within 30 minutes.</li>
              <li>Watch your inbox at <span className="font-semibold">{f.contact_email}</span> for a confirmation from <span className="font-semibold">supplychain@apexscs.ca</span>.</li>
              <li>Need to reach us immediately? Call <span className="font-semibold">778-861-7890</span>.</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  const nextStep = () => setStep(Math.min(step + 1, 4));
  const prev = () => setStep(Math.max(step - 1, 1));
  const progress = (step / 4) * 100;

  return (
    <div className="bg-gray-50 min-h-screen py-16 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="font-label text-xs text-[#2E7BC7]">Request a Quote</div>
        <h1 className="mt-2 font-display font-black text-3xl md:text-5xl text-[#0B3D6E]">Tell us about your freight.</h1>
        <p className="mt-3 text-slate-600">Our operations team will review your request and reach out by phone or email with a firm rate — typically within 30 minutes during business hours.</p>

        <div className="mt-8 h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full bg-[#2E7BC7] transition-all" style={{ width: `${progress}%` }} />
        </div>
        <div className="mt-2 text-xs font-label text-slate-500">Step {step} of 4</div>

        <div className="mt-8 bg-white border border-gray-200 rounded-lg p-8">
          {step === 1 && (
            <div className="space-y-5" data-testid="quote-step-1">
              <SectionTitle>Origin & Destination</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <FieldSet legend="Pickup">
                  <FieldRow>
                    <Field label="City" v={f.pickup_city} on={upd("pickup_city")} test="q-pickup-city" />
                    <Field label="State" v={f.pickup_state} on={upd("pickup_state")} test="q-pickup-state" />
                  </FieldRow>
                  <FieldRow>
                    <Field label="ZIP" v={f.pickup_zip} on={upd("pickup_zip")} test="q-pickup-zip" />
                    <Field label="Date" type="date" v={f.pickup_date} on={upd("pickup_date")} test="q-pickup-date" />
                  </FieldRow>
                </FieldSet>
                <FieldSet legend="Delivery">
                  <FieldRow>
                    <Field label="City" v={f.delivery_city} on={upd("delivery_city")} test="q-delivery-city" />
                    <Field label="State" v={f.delivery_state} on={upd("delivery_state")} test="q-delivery-state" />
                  </FieldRow>
                  <FieldRow>
                    <Field label="ZIP" v={f.delivery_zip} on={upd("delivery_zip")} test="q-delivery-zip" />
                    <Field label="Date" type="date" v={f.delivery_date} on={upd("delivery_date")} test="q-delivery-date" />
                  </FieldRow>
                </FieldSet>
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-5" data-testid="quote-step-2">
              <SectionTitle>Freight & Equipment</SectionTitle>
              <div className="grid md:grid-cols-2 gap-5">
                <SelectField label="Service Type" value={f.service_type} onChange={(v) => setF({ ...f, service_type: v })} options={SVC} test="q-service-type" />
                <SelectField label="Equipment" value={f.equipment_type} onChange={(v) => setF({ ...f, equipment_type: v })} options={EQUIP} test="q-equipment" />
              </div>
              <Field label="Commodity" v={f.commodity} on={upd("commodity")} test="q-commodity" />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Field label="Weight (lbs)" type="number" v={f.weight_lbs} on={upd("weight_lbs")} test="q-weight" />
                <Field label="Length (ft)" type="number" v={f.length_ft} on={upd("length_ft")} test="q-length" />
                <Field label="Width (ft)" type="number" v={f.width_ft} on={upd("width_ft")} test="q-width" />
                <Field label="Height (ft)" type="number" v={f.height_ft} on={upd("height_ft")} test="q-height" />
              </div>
              <Field label="Pieces" type="number" v={f.pieces} on={upd("pieces")} test="q-pieces" />
              <div>
                <label className="text-sm font-semibold text-slate-700 mb-1 block">Special Instructions</label>
                <textarea rows={3} value={f.special_instructions} onChange={upd("special_instructions")}
                  className="w-full bg-white border border-gray-300 rounded-md px-4 py-2 focus:ring-2 focus:ring-[#2E7BC7] focus:border-transparent outline-none"
                  data-testid="q-instructions" />
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="space-y-5" data-testid="quote-step-3">
              <SectionTitle>Attachments (optional)</SectionTitle>
              <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-10 cursor-pointer hover:border-[#2E7BC7] transition-colors">
                <Upload className="h-8 w-8 text-slate-400" />
                <div className="mt-3 text-sm text-slate-600">{file ? file.name : "Attach BOL, packing list, or freight photos"}</div>
                <input type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="q-file-input" />
              </label>
            </div>
          )}
          {step === 4 && (
            <div className="space-y-5" data-testid="quote-step-4">
              <SectionTitle>Contact</SectionTitle>
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Full Name" v={f.contact_name} on={upd("contact_name")} test="q-contact-name" />
                <Field label="Company" v={f.company_name} on={upd("company_name")} test="q-company" />
                <Field label="Email" type="email" v={f.contact_email} on={upd("contact_email")} test="q-contact-email" />
                <Field label="Phone" v={f.contact_phone} on={upd("contact_phone")} test="q-contact-phone" />
              </div>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between">
            <button onClick={prev} disabled={step === 1}
              className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 disabled:opacity-40"
              data-testid="q-prev-btn"><ChevronLeft className="h-4 w-4" /> Back</button>
            {step < 4 ? (
              <button onClick={nextStep} className="btn-primary-apex inline-flex items-center gap-2" data-testid="q-next-btn">
                Continue <ChevronRight className="h-4 w-4" />
              </button>
            ) : (
              <button onClick={submit} disabled={loading} className="btn-primary-apex" data-testid="q-submit-btn">
                {loading ? "Submitting..." : "Submit Quote Request"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const SectionTitle = ({ children }) => <div className="font-display font-bold text-xl text-[#0B3D6E] mb-2">{children}</div>;
const FieldSet = ({ legend, children }) => (
  <div className="border border-gray-200 rounded-md p-5">
    <div className="font-label text-xs text-[#2E7BC7] mb-3">{legend}</div>
    <div className="space-y-3">{children}</div>
  </div>
);
const FieldRow = ({ children }) => <div className="grid grid-cols-2 gap-3">{children}</div>;
function Field({ label, v, on, type = "text", test }) {
  return (
    <div>
      <label className="text-sm font-semibold text-slate-700 mb-1 block">{label}</label>
      <input type={type} value={v} onChange={on}
        className="w-full bg-white border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-[#2E7BC7] focus:border-transparent outline-none"
        data-testid={test} />
    </div>
  );
}
function SelectField({ label, value, onChange, options, test }) {
  return (
    <div>
      <label className="text-sm font-semibold text-slate-700 mb-1 block">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={test}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}
